// Coordination -> Tracking. What the field team opens to answer "what is
// installed, what is not, and where".
//
// One filter row at the top scopes EVERYTHING below it - the drawing, the
// table and both charts read the same slice. Per-card filters would let
// the drawing and the chart disagree about the same question, which is
// how a dashboard stops being believed.
//
// See docs/tracking.md.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Select, DatePicker, Button, Table, Progress, Tag, message, Badge, Input,
  Drawer, Spin, Empty, Tooltip, Segmented, Dropdown,
} from "antd";
import {
  PrinterOutlined, ReloadOutlined, DiffOutlined, CloudUploadOutlined, BarChartOutlined,
  ShareAltOutlined, DownloadOutlined, DownOutlined, FileExcelOutlined, FilePdfOutlined, FileTextOutlined,
} from "@ant-design/icons";
import moment from "moment";
import CoordinationBreadcrumbs from "components/Coordination/CoordinationBreadcrumbs";
import useLastProject from "components/Coordination/useLastProject";
import { url, tokenRef } from "api";
import {
  getBoard, getAnalytics, setSegmentStatus, getMatches, decideMatch, applySourceGroup, createItem, exportBoard,
} from "api/tracking/fieldTrackingApi";
import TrackingLayout from "./TrackingLayout";
import TrackingCharts from "./TrackingCharts";
import TrackingReview from "./TrackingReview";
import TrackingSources from "./TrackingSources";
import TrackingFreshness from "./TrackingFreshness";
import ApplySpinner from "./ApplySpinner";
import useApplyRun from "./useApplyRun";
import ErrorBoundary from "components/ErrorBoundary/ErrorBoundary";
import ModelTracking from "./ModelTracking";
import ShareResourceModal from "components/Share/ShareResourceModal";
import { usePreference } from "helpers/userPrefs";
import {
  STATUS_ORDER, STATUS_COLOR, STATUS_SHORT, STATUS_LABEL, systemLabel,
} from "./trackingTheme";
import { matchOption } from "components/SearchSelect/searchSelect";

const { RangePicker } = DatePicker;

// WHAT THIS CALLER MAY DO HERE (board.perms, 2026-10-14: every coordination
// tab has View / Add / Edit / Delete keys). An older server without `perms`
// reads as before: canEdit for the management controls, statuses for all.
function permsOf(board) {
  if (board && board.perms) return board.perms;
  const e = !!(board && board.canEdit);
  return { view: true, add: e, edit: e, delete: e, setStatus: true, partner: false };
}
const addSetAllowed = (pm) => !!(pm.partner || pm.add);

export default function TrackingPage() {
  const { projectId: routeProjectId } = useParams();
  const history = useHistory();
  const userInfo = useSelector((s) => s.user?.userInfo);
  const currentUserId = userInfo?._id || userInfo?.id;

  const [projects, setProjects] = useState([]);
  // The same project memory every other coordination page uses, so
  // Tracking opens on the job you were already working on and a
  // ?projectId= deep link still wins for that visit.
  const [rememberedId, setRememberedId] = useLastProject(projects);
  const projectId = routeProjectId || rememberedId || "";
  const [board, setBoard] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  // True from the first render when the project is already known: starting
  // false mounted the Model tab, swapped it for the spinner a moment later
  // and mounted it again - two streaming sessions of the whole model.
  const [loading, setLoading] = useState(!!projectId);
  const [refetching, setRefetching] = useState(false);

  // The filter row. One slice, read by the drawing, the table and both
  // charts.
  const [range, setRange] = useState([null, null]);
  // Status and system filters remembered per person and per project
  // (helpers/userPrefs, owner 2026-09-18: "by user").
  const [filterPref, setFilterPref] = usePreference("tracking.filters", {}, { projectId: projectId || "" });
  const statusFilter = (filterPref && filterPref.status) || "all";
  const systemFilter = (filterPref && filterPref.system) || "all";
  const setStatusFilter = (v) => setFilterPref((f) => ({ ...(f || {}), status: v }));
  const setSystemFilter = (v) => setFilterPref((f) => ({ ...(f || {}), system: v }));
  const [visibleSystems, setVisibleSystems] = useState([]);
  const [sheetId, setSheetId] = useState("");
  const [reviewOpen, setReviewOpen] = useState(false);
  const [sourcesOpen, setSourcesOpen] = useState(false);
  // THE APPLY THAT IS RUNNING, IF ONE IS. Polled while live and not at
  // all otherwise. Declared here, above everything that reads it, because
  // a const read before its declaration is a temporal dead zone and this
  // page has no error boundary above it - the Model tab went white that
  // way on 2026-09-09.
  const { run: applyRun, follow: followApply, unreachable: applyUnreachable } = useApplyRun(projectId);
  const [shareOpen, setShareOpen] = useState(false);
  // Analytics sits at the TOP behind a toggle, the same shape the issues
  // board uses - so the two coordination boards are read the same way.
  // Off by default: the field opens this page to work the drawing, not
  // to read charts.
  const [showAnalytics, setShowAnalytics] = usePreference("tracking.showAnalytics", false);
  // Two ways of looking at the same job. The drawing answers "where is
  // this run and how far along is it"; the model answers "what is in the
  // model and what has the field done to it". They read the same
  // statuses, so neither is a second board.
  // The model is where the field marks things installed, so it is the
  // tab the page opens on (owner, 2026-09-15); the drawing stays one
  // click away.
  const [view, setView] = usePreference("tracking.view", "model");
  const [matches, setMatches] = useState([]);
  const firstLoad = useRef(true);
  // The table's page. The server pages and filters: a 35,000-item board
  // used to arrive whole (21 MB) and be filtered in the browser on every
  // keystroke. Page one is fetched with the board; the rest on demand.
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = usePreference("tracking.pageSize", 50);
  const [search, setSearch] = useState("");
  const [searchDraft, setSearchDraft] = useState("");
  const [boardExporting, setBoardExporting] = useState("");

  useEffect(() => {
    let alive = true;
    // The picker endpoint, not the full project list: this is a dropdown,
    // and it is what the rest of the coordination suite reads.
    fetch(`${url}/projects/picker`, { headers: { Authorization: `Bearer ${localStorage.getItem(tokenRef)}` } })
      .then((r) => r.json())
      .then((rows) => {
        if (!alive) return;
        setProjects(Array.isArray(rows) ? rows : (rows?.projects || []));
      })
      .catch(() => { if (alive) setProjects([]); });
    return () => { alive = false; };
  }, []);

  // WHEN THE APPLY FINISHES, THE NUMBERS HAVE TO MOVE. The job wrote
  // thousands of rows in another process, so nothing on this page knows
  // yet. Keyed on the runId AND the status, so it fires once per run
  // reaching a terminal state and not on every poll - and it fires for a
  // FAILED or STOPPED run too, because the parts that did apply are real.
  const lastSettled = useRef("");
  useEffect(() => {
    if (!applyRun) return;
    // RELOAD WHEN IT STARTS, TOO - not only when it ends.
    //
    // The open count is suppressed per person by the SERVER while a run is
    // live, so the badge only picks that up on a /board read. Without this
    // the number the owner is watching keeps its pre-apply value for the
    // whole six to eight minutes - which is the exact number that told him
    // his apply had failed. Reloading only on settle would have fixed the
    // end of the job and left the middle of it looking identical to
    // before.
    const key = `${applyRun.runId}:${applyRun.status}:${applyRun.live ? "live" : ""}:${applyRun.stale ? "stale" : ""}`;
    if (lastSettled.current === key) return;
    lastSettled.current = key;
    // eslint-disable-next-line no-use-before-define
    load();
  }, [applyRun]); // eslint-disable-line react-hooks/exhaustive-deps

  const load = useCallback(async () => {
    if (!projectId) return;
    if (firstLoad.current) setLoading(true); else setRefetching(true);
    try {
      const filter = {
        page, limit: pageSize,
        from: range[0] ? range[0].format("YYYY-MM-DD") : undefined,
        to: range[1] ? range[1].format("YYYY-MM-DD") : undefined,
        system: systemFilter,
        status: statusFilter,
        q: search || undefined,
      };
      const [b, a] = await Promise.all([
        getBoard(projectId, filter),
        getAnalytics(projectId, {
          from: range[0] ? range[0].format("YYYY-MM-DD") : undefined,
          to: range[1] ? range[1].format("YYYY-MM-DD") : undefined,
          system: systemFilter,
        }),
      ]);
      setBoard(b);
      setAnalytics(a);
      setVisibleSystems((prev) => {
        // The systems on the whole board, from the totals - not from
        // whichever page the table happens to be on.
        const present = [...new Set((b.systems || []).map((s) => s.system || "other"))];
        // Keep what the user turned off; add anything newly present.
        return firstLoad.current ? present : [...new Set([...prev.filter((s) => present.includes(s)), ...present.filter((s) => !prev.length)])];
      });
      setSheetId((prev) => prev || String((b.sheets || [])[0]?._id || ""));
    } catch (e) {
      message.error(e.message || "Could not load tracking");
    } finally {
      firstLoad.current = false;
      setLoading(false);
      setRefetching(false);
    }
  }, [projectId, range, systemFilter, statusFilter, search, page, pageSize]);

  useEffect(() => { firstLoad.current = true; }, [projectId]);

  // THE CARDS ONLY, after a status write on the model view (owner,
  // 2026-10-19: the counts "are not reflecting real time changes"). Not
  // load(): a new board object would reach the model view as new props.
  const loadAnalytics = useCallback(async () => {
    if (!projectId) return;
    try {
      setAnalytics(await getAnalytics(projectId, {
        from: range[0] ? range[0].format("YYYY-MM-DD") : undefined,
        to: range[1] ? range[1].format("YYYY-MM-DD") : undefined,
        system: systemFilter,
      }));
    } catch (e) { /* the cards keep their last numbers */ }
  }, [projectId, range, systemFilter]);
  useEffect(() => { load(); }, [load]);
  // A new filter starts from page one.
  useEffect(() => { setPage(1); }, [projectId, range, systemFilter, statusFilter, search]);

  const openReview = async () => {
    try {
      const r = await getMatches(projectId);
      setMatches(r.matches || []);
      setReviewOpen(true);
    } catch (e) { message.error(e.message || "Could not load the proposals"); }
  };

  const onSetSegment = async (item, segment, body) => {
    try {
      await setSegmentStatus(projectId, item._id, segment.key, body);
      message.success(`${item.mark || "Item"} · ${STATUS_SHORT[body.status]}`);
      await load();
    } catch (e) {
      message.error(e.message || "Could not update");
      throw e;
    }
  };

  // Already filtered and paged by the server; the page IS the filter.
  const items = board?.items || [];
  const total = board?.total || 0;
  // What the overlay draws: every item with geometry, whatever page the
  // table is on, narrowed by the same system and status the table is.
  const drawn = useMemo(() => (board?.drawn || []).filter((i) => {
    if (systemFilter !== "all" && (i.system || "other") !== systemFilter) return false;
    if (statusFilter !== "all" && i.rollupStatus !== statusFilter) return false;
    return true;
  }), [board, systemFilter, statusFilter]);

  const systemsPresent = useMemo(
    () => [...new Set((board?.systems || []).map((s) => s.system || "other"))],
    [board]
  );

  const columns = [
    {
      title: "Item", dataIndex: "mark", width: 150, fixed: "left",
      render: (v, r) => (
        <div>
          <div style={{ fontWeight: 600, fontSize: 12.5, color: "#23343E" }}>{v || r.name || "—"}</div>
          <div style={{ fontSize: 10.5, color: "#8D8D8D" }}>
            {systemLabel(r.system)}{r.size ? ` · ${r.size}` : ""}{r.grid ? ` · ${r.grid}` : ""}
          </div>
        </div>
      ),
      sorter: (a, b) => String(a.mark || "").localeCompare(String(b.mark || "")),
    },
    {
      title: "Level", dataIndex: "level", width: 80,
      render: (v) => <span style={{ fontSize: 12 }}>{v || "—"}</span>,
    },
    {
      title: "Progress", key: "progress", width: 190,
      sorter: (a, b) => a.progress.percentInstalled - b.progress.percentInstalled,
      render: (_, r) => (
        <Tooltip title={r.progress.weighting === "count"
          ? "No lengths on this run, so the percentage counts segments"
          : `${r.progress.byStatus.installed} of ${r.progress.total} ft installed`}>
          <div>
            <Progress
              percent={r.progress.percentInstalled}
              // The second bar is work started but not finished, so the
              // row shows both without needing a second column.
              success={{ percent: r.progress.percentInstalled }}
              strokeColor={STATUS_COLOR.installed}
              size="small" style={{ marginBottom: 0 }}
            />
            <span style={{ fontSize: 10, color: "#8D8D8D" }}>
              {r.progress.percentStarted !== r.progress.percentInstalled
                ? `${r.progress.percentStarted}% started` : ""}
              {r.progress.weighting === "count" ? " · by count" : ""}
            </span>
          </div>
        </Tooltip>
      ),
    },
    {
      title: "Status", key: "status", width: 170,
      render: (_, r) => (
        <Select
          size="small" style={{ width: 155 }} value={r.rollupStatus}
          disabled={!permsOf(board).setStatus}
          options={STATUS_ORDER.map((s) => ({
            value: s,
            label: (
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                <span style={{ width: 9, height: 9, borderRadius: 2, background: STATUS_COLOR[s] }} />
                {STATUS_LABEL[s]}
              </span>
            ),
          }))}
          onChange={async (v) => {
            // An override from the table sets every segment, with today's
            // date - the same rule as the drawing: status and date
            // together, never one without the other.
            try {
              for (const seg of r.segments || []) {
                // eslint-disable-next-line no-await-in-loop
                await setSegmentStatus(projectId, r._id, seg.key, { status: v, statusDate: moment().format("YYYY-MM-DD") });
              }
              message.success(`${r.mark || "Item"} · ${STATUS_SHORT[v]}`);
              load();
            } catch (e) { message.error(e.message || "Could not update"); }
          }}
        />
      ),
    },
    {
      title: "Date", key: "date", width: 130,
      render: (_, r) => {
        const dates = (r.segments || []).map((s) => s.statusDate).filter(Boolean).sort();
        const last = dates[dates.length - 1];
        return (
          <DatePicker
            size="small" style={{ width: 120 }} allowClear={false}
            value={last ? moment(last) : null}
            placeholder="Set date"
            onChange={async (d) => {
              if (!d) return;
              try {
                for (const seg of r.segments || []) {
                  // eslint-disable-next-line no-await-in-loop
                  await setSegmentStatus(projectId, r._id, seg.key, { statusDate: d.format("YYYY-MM-DD") });
                }
                load();
              } catch (e) { message.error(e.message || "Could not update"); }
            }}
          />
        );
      },
    },
    {
      title: "Spans", key: "segs", width: 74,
      render: (_, r) => <Tag style={{ margin: 0 }}>{(r.segments || []).length}</Tag>,
    },
  ];

  const totals = analytics?.totals;
  // The drawing's totals as cards, so the model view can show them in
  // the same row as its own numbers instead of a second bar above them.
  const totalCards = useMemo(() => (totals ? [
    ["Tracked", `${totals.items}`, "items"],
    ["Installed", `${totals.percentInstalled}%`, `${totals.installed} of ${totals.total} ft`],
    ["Remaining", `${Math.round((totals.total - totals.installed) * 10) / 10}`, "ft"],
  ].map(([label, value, sub]) => (
    <div key={label} style={{ background: "#fff", border: "1px solid #EFEFEF", borderRadius: 6, padding: "8px 14px", minWidth: 132 }}>
      <div style={{ fontSize: 10.5, fontWeight: 600, color: "#8D8D8D", letterSpacing: ".06em" }}>{label.toUpperCase()}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color: "#23343E", lineHeight: 1.25 }}>{value}</div>
      <div style={{ fontSize: 11, color: "#8D8D8D" }}>{sub}</div>
    </div>
  )) : null), [totals]);

  return (
    <div style={{ background: "#FAFAFA", minHeight: "calc(100 * var(--vh))", padding: "10px 20px 24px" }}>
      <CoordinationBreadcrumbs items={[{ label: "Coordination", to: "/coordination" }]} current="Tracking" />

      {/* ONE filter row, above everything it scopes. */}
      <div style={{
        display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
        background: "#fff", border: "1px solid #EFEFEF", borderRadius: 6, padding: "10px 14px", marginBottom: 12,
      }}>
        <span style={{ fontSize: 10.5, fontWeight: 600, color: "#8D8D8D", letterSpacing: ".06em" }}>PROJECT</span>
        <Select filterOption={matchOption}
          showSearch style={{ minWidth: 240 }} size="small"
          value={projectId || undefined}
          placeholder="Choose a project"
          optionFilterProp="label"
          options={projects.map((p) => ({ value: String(p._id), label: p.title || p.name || "Untitled" }))}
          onChange={(v) => {
            setRememberedId(v);
            if (routeProjectId) history.push(`/tracking/${v}`);
          }}
        />
        <span style={{ fontSize: 10.5, fontWeight: 600, color: "#8D8D8D", letterSpacing: ".06em" }}>SPAN</span>
        <RangePicker size="small" value={range} onChange={(v) => setRange(v || [null, null])} />
        {/* ACTIVITY STATUS, not element status (leader + Tom, 2026-09-28).
            This select is `statusFilter`, and it filters THE ACTIVITY BOARD -
            board items whose rollupStatus does not match are dropped. It has
            never touched model elements.
            But labelled "STATUS", on a bar above a 3D model, offering exactly
            the four ELEMENT statuses, it reads as a model filter to everybody
            who meets it: Tom reached for it while verifying the 3D filter, I
            reached for it before him, and it fired zero field-tracking requests
            and installed no hidden rule - correctly, and confusingly. The model
            filter lives in the quantities table's column headers.
            One word, so nobody spends another hour on the wrong control. */}
        <span style={{ fontSize: 10.5, fontWeight: 600, color: "#8D8D8D", letterSpacing: ".06em" }}>ACTIVITY STATUS</span>
        <Select
          size="small" style={{ minWidth: 160 }} value={statusFilter} onChange={setStatusFilter}
          options={[{ value: "all", label: "All statuses" },
            ...STATUS_ORDER.map((s) => ({ value: s, label: STATUS_LABEL[s] }))]}
        />
        <Select
          size="small" style={{ minWidth: 140 }} value={systemFilter} onChange={setSystemFilter}
          options={[{ value: "all", label: "All systems" },
            ...systemsPresent.map((s) => ({ value: s, label: systemLabel(s) }))]}
        />
        <Segmented
          size="small"
          value={view}
          onChange={setView}
          options={[{ label: "Drawing", value: "drawing" }, { label: "Model", value: "model" }]}
        />
        <span style={{ flex: 1 }} />
        {!!board?.openMatches && (
          <Badge count={board.openMatches} offset={[-2, 2]}>
            <Button size="small" icon={<DiffOutlined />} onClick={openReview}>
              Review sources
            </Button>
          </Badge>
        )}
        {/* NEXT TO REVIEW, ALWAYS. A group apply runs on the worker now, so
            the button returns at once and this is the only thing that says
            the work is still going - or that it stopped. */}
        <ApplySpinner run={applyRun} where="review" unreachable={applyUnreachable} />
        <Button
          size="small" icon={<BarChartOutlined />}
          onClick={() => setShowAnalytics((v) => !v)}
          style={showAnalytics
            ? { background: "#3A5765", borderColor: "#3A5765", color: "#fff" }
            : { color: "#3A5765" }}
        >
          Analytics
        </Button>
        <Button size="small" icon={<CloudUploadOutlined />} onClick={() => setSourcesOpen(true)} disabled={!projectId}>
          Sources
        </Button>
        {/* AND NEXT TO SOURCE, BUT ONLY WHEN THE WORK CAME FROM ONE
            (owner's rule). An apply started from the Review drawer is not
            a source's business, and a second spinner that was always on
            would stop meaning anything. */}
        <ApplySpinner run={applyRun} where="source" unreachable={applyUnreachable} />
        {/* Sharing the board with the trade that is doing the work.
            Signed-in only, like the issue manager: the point of this page
            is the people on site marking what they installed, which a
            read-only copy cannot do. Owner side only - and the server
            refuses it too, this only stops us showing a partner a button
            that 403s. */}
        {board?.canEdit && (
          <Button size="small" icon={<ShareAltOutlined />} onClick={() => setShareOpen(true)} disabled={!projectId}>
            Share with a partner
          </Button>
        )}
        <Button size="small" icon={<ReloadOutlined />} onClick={load} loading={refetching}>Refresh</Button>
        <Button
          size="small" type="primary" icon={<PrinterOutlined />}
          disabled={!projectId}
          onClick={() => {
            const q = new URLSearchParams();
            if (range[0]) q.set("from", range[0].format("YYYY-MM-DD"));
            if (range[1]) q.set("to", range[1].format("YYYY-MM-DD"));
            if (systemFilter !== "all") q.set("system", systemFilter);
            if (statusFilter !== "all") q.set("status", statusFilter);
            window.open(`/tracking-print/${projectId}${q.toString() ? `?${q}` : ""}`, "_blank");
          }}
        >
          Print progress PDF
        </Button>
        {/* EVERY ITEM AND EVERY SPAN (owner, 2026-10-20): the print above is
            the first 1000 items as a picture; these are the whole board under
            the same filters, streamed by the server. */}
        <Dropdown
          trigger={["click"]}
          disabled={!projectId || !!boardExporting}
          menu={{
            onClick: async ({ key: ext }) => {
              const NAMES = { pdf: "PDF", xlsx: "Excel", csv: "CSV" };
              setBoardExporting(ext);
              try {
                const r = await exportBoard(projectId, ext, {
                  from: range[0] ? range[0].format("YYYY-MM-DD") : undefined,
                  to: range[1] ? range[1].format("YYYY-MM-DD") : undefined,
                  system: systemFilter,
                  status: statusFilter,
                  q: search || undefined,
                });
                message.success(`${NAMES[ext]} downloaded - ${r.count.toLocaleString()} item${r.count === 1 ? "" : "s"}`);
              } catch (e) {
                message.error(e.message || `${NAMES[ext]} export failed`);
              } finally {
                setBoardExporting("");
              }
            },
            items: [
              { key: "pdf", icon: <FilePdfOutlined />, label: "PDF - every item, open spans" },
              { key: "xlsx", icon: <FileExcelOutlined />, label: "Excel - summary, items, every span" },
              { key: "csv", icon: <FileTextOutlined />, label: "CSV - one row per span" },
            ],
          }}
        >
          <Button size="small" icon={<DownloadOutlined />} loading={!!boardExporting} title="Every tracked item on the board, with these filters">
            {boardExporting ? "Building…" : "Export PDF / Excel / CSV"} <DownOutlined style={{ fontSize: 10 }} />
          </Button>
        </Dropdown>
      </div>

      {/* LAST UPDATED (owner, 2026-10-17): when the model data and the tracking
          quantities were last updated, in words, under the bar with Sources.
          Re-read on Refresh (refetching flips). */}
      <TrackingFreshness projectId={projectId} refreshKey={refetching} />

      {loading ? (
        <div style={{ textAlign: "center", padding: 60 }}><Spin size="large" /></div>
      ) : !projectId ? (
        <Empty description="Pick a project to start" />
      ) : (
        <>
          {/* ONE ROW OF NUMBERS, NOT TWO. On the model view these cards
              are handed to ModelTracking and drawn beside its own; here
              they are the row. */}
          {totals && view !== "model" && (
            <div style={{ display: "flex", gap: 12, marginBottom: 12, flexWrap: "wrap" }}>{totalCards}</div>
          )}

          {showAnalytics && (
            <div style={{ marginBottom: 12 }}>
              <TrackingCharts
                analytics={analytics}
                loading={refetching}
                // WHICH OF THIS PAGE'S CONTROLS THE PARTS PANEL CANNOT
                // FOLLOW, named so it can say so. A part has no system of
                // its own and the panel is a current-state reading, so the
                // system picker and the date range do not narrow it -
                // and a number that quietly ignores the control above it
                // is the one people cannot reconcile.
                narrowed={[
                  ...(systemFilter && systemFilter !== "all" ? ["the system picker"] : []),
                  ...(range && range[0] ? ["the date range"] : []),
                ]}
              />
            </div>
          )}

          {/* Drawing and table side by side: the table IS the drawing's
              table view, so every value on the overlay is also readable
              as text without hovering anything. */}
          <div style={{ display: view === "drawing" ? "grid" : "none", gridTemplateColumns: "minmax(0, 1.35fr) minmax(0, 1fr)", gap: 12, alignItems: "start", marginBottom: 12 }}>
            <TrackingLayout
              items={drawn}
              total={total}
              unplaced={board?.unplaced || 0}
              sheets={board?.sheets || []}
              grids={board?.grids || []}
              canEdit={permsOf(board).setStatus}
              canAdd={permsOf(board).add}
              onSetSegment={onSetSegment}
              onCreateItem={async (body) => { await createItem(projectId, body); await load(); }}
              visibleSystems={visibleSystems.length ? visibleSystems : systemsPresent}
              onToggleSystem={(s) => setVisibleSystems((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]))}
              sheetId={sheetId}
              onSheetChange={setSheetId}
              shownView={view === "drawing"}
            />
            <div style={{ background: "#fff", border: "1px solid #EFEFEF", borderRadius: 6, overflow: "hidden" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", borderBottom: "1px solid #EFEFEF" }}>
                <Input.Search
                  size="small"
                  allowClear
                  placeholder="Find a mark, name, size, grid or level"
                  value={searchDraft}
                  onChange={(e) => { setSearchDraft(e.target.value); if (!e.target.value) setSearch(""); }}
                  onSearch={(v) => setSearch(v.trim())}
                  style={{ maxWidth: 360 }}
                />
                <span style={{ flex: 1 }} />
                <span style={{ fontSize: 11, color: "#8D8D8D" }}>
                  {total.toLocaleString()} item{total === 1 ? "" : "s"}{search ? ` for "${search}"` : ""}
                </span>
              </div>
              <Table
                size="small"
                rowKey="_id"
                dataSource={items}
                columns={columns}
                loading={refetching}
                pagination={{
                  current: page, pageSize, total, size: "small",
                  showSizeChanger: true, pageSizeOptions: ["25", "50", "100", "200"],
                  showTotal: (n, r) => `${r[0].toLocaleString()}–${r[1].toLocaleString()} of ${n.toLocaleString()}`,
                  onChange: (p, ps) => { if (ps !== pageSize) { setPageSize(ps); setPage(1); } else setPage(p); },
                }}
                scroll={{ x: 760 }}
                locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Nothing matches this filter" /> }}
              />
            </div>
          </div>

          {/* Kept mounted but hidden when the drawing is showing: the
              model view holds a WebGL context and a streaming session,
              and tearing those down on every switch would reload the
              whole model to come back to it. */}
          {view === "model" && (
            <ErrorBoundary name="Construction tracking · model">
              <ModelTracking
                projectId={projectId}
                project={board?.project || null}
                sheets={board?.sheets || []}
                canEdit={!!board?.canEdit}
                canAdd={permsOf(board).add}
                canDelete={permsOf(board).delete}
                canAddSet={addSetAllowed(permsOf(board))}
                statsPrefix={totalCards}
                onStatusWritten={loadAnalytics}
              />
            </ErrorBoundary>
          )}

        </>
      )}

      <Drawer
        title="Sources — a schedule, a layout, or the model"
        width={980}
        open={sourcesOpen}
        onClose={() => setSourcesOpen(false)}
        destroyOnClose
      >
        <TrackingSources
          projectId={projectId}
          canAdd={permsOf(board).add}
          canDelete={permsOf(board).delete}
          sheets={board?.sheets || []}
          // The two shared facts, from the board: what is being applied
          // right now and what last failed. Everybody sees both; only the
          // person who started a run has it taken off their count, and the
          // SERVER does that - the client is never handed a number it has
          // to adjust.
          processing={board?.processing || []}
          applyFailures={board?.applyFailures || []}
          onChanged={load}
        />
      </Drawer>

      <Drawer
        title="Review what a source proposes"
        width={860}
        open={reviewOpen}
        onClose={() => setReviewOpen(false)}
        destroyOnClose
      >
        <TrackingReview
          projectId={projectId}
          matches={matches}
          canApply={permsOf(board).edit}
          onApply={async (matchId, decisions) => {
            const r = await decideMatch(projectId, matchId, decisions);
            message.success(`Applied — ${r.linked} linked, ${r.added} added`);
            setReviewOpen(false);
            await load();
          }}
          // THE WHOLE IMPORT IN ONE CLICK. It runs for minutes, so the
          // result says what each part did rather than one total: a group
          // where part 3 failed and 4 to 11 never ran is a different
          // situation from one that finished, and a single "applied N"
          // would read the same for both.
          onApplyGroup={async (groupId, expected) => {
            // IT STARTS THE JOB, IT DOES NOT DO IT. The route answers 202
            // with a runId; there are no totals to print yet, and the
            // version of this that printed `r.linked` would now say 0.
            // What the person gets instead is the spinner next to Review,
            // and the numbers when it lands.
            const r = await applySourceGroup(projectId, groupId, "review");
            const n = r.partsInGroup || expected || 0;
            message.info(
              `Applying ${n} part${n === 1 ? "" : "s"} in the background`
              + `${r.lane === "inline" ? " (in this process - the worker is not running)" : ""}`
              + ". You can close this; the spinner next to Review follows it.",
              6,
            );
            setReviewOpen(false);
            // Show the spinner on the click rather than up to a poll
            // later: that gap is where somebody presses the button again,
            // and eight of those at once is what took the API down.
            await followApply();
          }}
        />
      </Drawer>

      <ShareResourceModal
        type="tracking"
        id={projectId}
        projectId={projectId}
        title={`${(projects.find((p) => String(p._id) === String(projectId)) || {}).title || "Project"} - field tracking`}
        open={shareOpen}
        onClose={() => setShareOpen(false)}
      />
    </div>
  );
}
